import React from "react";
import { graphql, useFragment } from "react-relay";

import type { ActionItemIntegration, BaseIntegrationDetails } from "~/integrations/actionItemIntegration";
import { ClickupLogo } from "~/integrations/clickup/ClickupLogo";
import { CreateNewExternalTicketPane } from "~/integrations/clickup/CreateNewExternalTicketPane";
import { ImportExistingTicketPane } from "~/integrations/clickup/ImportExistingTicketPane";
import { ClickupActionItemIntegrationLinkWidget } from "~/integrations/clickup/Widget";
import { promptClickupAuth } from "~/lib/auth";
import type { IntegrationConfigProps, IntegrationDetailsType } from "~/stream/StreamSettings/ActionItemIntegrations";

import { ClickupStreamSettingsConfigView, ClickupStreamSettingsIntegrationCard } from "./ClickupStreamSettings";

import type { clickupStreamDetails$key } from "~/__generated__/clickupStreamDetails.graphql";

import styles from "~/integrations/actionItemIntegration/css/styles.module.css";

graphql`
	fragment clickupSpecificData on ClickupActionItemIntegrationSpecificDataNode {
		__typename
		externalTaskId
		listName
		status
		statusColor
	}
`;

const clickupStreamDetails = graphql`
	fragment clickupStreamDetails on ClickupStreamIntegrationNode {
		clickupUser {
			id
			user {
				fullName
			}
		}
		externalListName
		includeAiGenerated
		externalListId
		externalWorkspaceId
		externalSpaceId
		externalFolderId
	}
`;

export interface ClickupStreamIntegrationDetails extends BaseIntegrationDetails {
	externalListId: string;
	externalWorkspaceId: string;
	externalSpaceId: string;
	externalFolderId: string;
}

function useClickupStreamIntegrationDetails(integrationOrAsanaStream: IntegrationDetailsType) {
	if (!("actionItemIntegration" in integrationOrAsanaStream) || !integrationOrAsanaStream.actionItemIntegration) {
		throw new Error("Stream integration details are for Asana, not ClickUp"); // Should never encounter this
	}

	const streamDetails = useFragment<clickupStreamDetails$key>(
		clickupStreamDetails,
		integrationOrAsanaStream.actionItemIntegration.integrationSpecificDetails,
	);

	return {
		containerTitle: streamDetails.externalListName,
		createdBy: streamDetails.clickupUser.user.fullName,
		includeAiGenerated: streamDetails.includeAiGenerated,
		externalListId: streamDetails.externalListId,
		externalWorkspaceId: streamDetails.externalWorkspaceId,
		externalSpaceId: streamDetails.externalSpaceId,
		externalFolderId: streamDetails.externalFolderId,
	};
}

function getClickupConfigurationPane({
	integration,
	stream,
	onFinish,
	onCancel,
	integrationDetails,
}: IntegrationConfigProps) {
	return (
		<ClickupStreamSettingsConfigView
			integration={integration}
			stream={stream}
			onFinish={onFinish}
			onCancel={onCancel}
			integrationDetails={integrationDetails as ClickupStreamIntegrationDetails}
		/>
	);
}

export const clickup: ActionItemIntegration = {
	key: "CLICKUP",
	logo: <ClickupLogo className={styles.integrationLogo} />,
	billingFeatureName: "clickup_integration",
	displayName: "ClickUp",
	externalActionItemName: "task",
	externalContainerType: "list",
	renderPill: ClickupActionItemIntegrationLinkWidget,
	renderCreateNewExternalTicketPane: CreateNewExternalTicketPane,
	renderImportExistingExternalTicketPane: ImportExistingTicketPane,
	promptAuth: promptClickupAuth,
	streamSettingsCard: ClickupStreamSettingsIntegrationCard,
	configurationPane: getClickupConfigurationPane,
	useIntegrationDetails: useClickupStreamIntegrationDetails,
};

export default clickup;
